import { z } from 'zod';

/**
 * Per-scene **asset spec** — the painted art, walkable polygon, and walker
 * perspective curve for a single painted scene. Lives next to the painting at
 * `assets/scenes/<scene-key>/spec.json` and is resolved through the journey
 * manifest's `assetPath` field.
 *
 * Coordinate convention: every spatial value is normalised to the painting's
 * logical canvas (x ∈ [0,1] left→right, y ∈ [0,1] top→bottom), matching the
 * journey manifest. PaintedScene multiplies by the rendered canvas size.
 */

import {
  NormalisedPointSchema,
  NormalisedPolygon3DSchema,
} from './journeyManifest.schema.js';

/**
 * Procedural fallback for layers without finished art. Renders a flat colour
 * fill at the layer's depth so the scene composes before its painting exists.
 * The optional label is printed once on the rect so QA can identify each
 * placeholder by id.
 */
export const PlaceholderLayerSchema = z.object({
  /** Hex colour (#RRGGBB). */
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/, 'color must be #RRGGBB'),
  /** Optional label drawn over the rect for debug visibility. */
  label: z.string().optional(),
});
export type PlaceholderLayer = z.infer<typeof PlaceholderLayerSchema>;

/**
 * Optional ambient animation hook — the layer drifts/sways/flickers on its
 * own clock. Implemented in `PaintedScene.update()`; pure data here so the
 * schema is portable.
 */
export const LayerAmbientSchema = z.object({
  /** Animation kind. `none` is the default and matches an absent block. */
  kind: z.enum([
    'parallax-drift', 'horizontal-sway', 'rotate-sway', 'alpha-flicker', 'linear-drift',
    'vertical-sway', 'breathing',
  ]),
  /** Amplitude in pixels (drift / sway), alpha delta (flicker / breathing), or scale delta (breathing Y). */
  amplitude: z.number().nonnegative(),
  /** Frequency in Hz. */
  frequencyHz: z.number().positive(),
  /** Optional phase offset in radians — useful to desync layers. */
  phaseRad: z.number().optional(),
  /** Secondary amplitude for compound kinds (e.g. breathing alpha delta). */
  secondaryAmplitude: z.number().nonnegative().optional(),
});
export type LayerAmbient = z.infer<typeof LayerAmbientSchema>;

/**
 * A single background layer painted over the scene. Either `src` or
 * `placeholder` must be present — `src` for finished art, `placeholder`
 * for stub layers that ship before art lands.
 */
export const SceneLayerSchema = z
  .object({
    /** Stable id (e.g. "sky", "trees", "foreground"). Used for parallax/lookup. */
    id: z.string().min(1),
    /** Asset URL relative to the public root. */
    src: z.string().min(1).optional(),
    /** Procedural fallback used when art is not yet painted. */
    placeholder: PlaceholderLayerSchema.optional(),
    /**
     * Parallax factor — 1.0 means the layer moves 1:1 with the camera, 0.0
     * means it stays fixed. Default 1.0 for static painted scenes.
     */
    parallax: z.number().min(0).max(2).default(1),
    /** Render depth. Higher = nearer the camera. */
    depth: z.number().int().default(0),
    /** Initial alpha. Default 1. */
    alpha: z.number().min(0).max(1).default(1),
    /** Optional ambient animation hook (single effect, backward compat). */
    ambient: LayerAmbientSchema.optional(),
    /** Multiple concurrent organic ambient effects (sway + breathing + etc.). */
    organicMotion: z.array(LayerAmbientSchema).optional(),
    /**
     * Depth bracket for N-bracket layering (0 = farthest, N = nearest camera).
     * When present on any layer, the scene uses bracket-based depth sorting
     * instead of fixed Phaser depth values.
     */
    depthBracket: z.number().int().nonnegative().optional(),
    /**
     * Legacy shorthand: "behind" = behind walker, "in-front" = in front.
     * Maps to depthBracket 0 / max when depthBracket is not explicit.
     */
    walkerRelation: z.enum(['behind', 'in-front']).optional(),
  })
  .refine(layer => Boolean(layer.src) !== Boolean(layer.placeholder), {
    message: 'Layer needs exactly one of src or placeholder',
    path: ['src'],
  });
export type SceneLayer = z.infer<typeof SceneLayerSchema>;

/**
 * Walker perspective curve — a sparse set of (y, scale) samples used to
 * scale the walker as she moves toward / away from the camera.
 *
 * The shared `perspectiveScale` helper interpolates between samples; samples
 * outside this list clamp to the nearest endpoint. y is in normalised scene
 * coords (top → bottom).
 */
export const PerspectiveSampleSchema = z.object({
  y: z.number().min(0).max(1),
  scale: z.number().positive().max(8),
});
export type PerspectiveSample = z.infer<typeof PerspectiveSampleSchema>;

export const NatureElementSchema = z.object({
  spriteKey: z.string().min(1),
  depthBracketRange: z.tuple([z.number().int().nonnegative(), z.number().int().nonnegative()]),
  baseDensity: z.number().nonnegative(),
  organicMotion: z.array(LayerAmbientSchema).default([]),
  materialType: z.string().min(1),
  baseScale: z.number().positive(),
  scaleJitter: z.tuple([z.number().positive(), z.number().positive()]),
  randomFlipX: z.boolean(),
  tintJitter: z.number().int().nonnegative().max(15).optional(),
});
export type NatureElement = z.infer<typeof NatureElementSchema>;

export const NatureConfigSchema = z.object({
  densityScale: z.number().min(0).max(3).default(1),
  seed: z.number().int().optional(),
  elements: z.array(NatureElementSchema).min(1),
  maxInstances: z.number().int().positive().default(40),
  exclusionZones: z.array(z.object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    w: z.number().min(0).max(1),
    h: z.number().min(0).max(1),
  })).optional(),
});
export type NatureConfig = z.infer<typeof NatureConfigSchema>;

/**
 * A single painted scene's asset spec. The walkable polygon is the artist's
 * authored path through the painting — irregular, possibly concave, drawn
 * with the path-editor tool at `public/dev/path-editor.html`.
 */
export const PaintedSceneAssetSpecSchema = z.object({
  /** Schema version. Bump on breaking shape changes. */
  version: z.union([z.literal(1), z.literal(2)]),

  /** Must match the scene key in the journey manifest. */
  key: z
    .string()
    .min(1)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'key must be lowercase-kebab'),

  /** Logical canvas size of the painting in pixels (used for editor preview). */
  canvas: z.object({
    width: z.number().int().positive(),
    height: z.number().int().positive(),
  }),

  /** Background art, painted in layers from far → near. */
  layers: z.array(SceneLayerSchema).min(1),

  /**
   * Artist-authored walkable region — point ring in normalised coords. The
   * polygon may be concave; vertex order may be CW or CCW. PaintedScene wraps
   * this in a `WalkablePolygon` and feeds it to `PolygonPathfinder` for
   * click-to-move.
   */
  walkablePolygon: NormalisedPolygon3DSchema,

  /** Walker perspective curve — sparse samples, interpolated at runtime. */
  perspective: z.array(PerspectiveSampleSchema).min(2),

  /** Optional canonical anchor for the walker's idle pose. */
  idleAnchor: NormalisedPointSchema.optional(),

  /**
   * Default depth bracket for the walker. Only used when layers define
   * depthBracket values. Layers with bracket > this render in front;
   * bracket < this render behind; bracket == this render behind the walker.
   */
  walkerDepthBracket: z.number().int().nonnegative().optional(),

  /**
   * Ascending normalised-Y thresholds that dynamically map walker Y to a
   * depth bracket. With N thresholds, there are N+1 brackets (0..N).
   * Y < thresholds[0] → bracket 0; Y >= thresholds[N-1] → bracket N.
   * When T6 lands, walker z replaces Y as the input.
   */
  depthBracketThresholds: z.array(z.number().min(0).max(1)).optional(),

  /**
   * How many viewports wide the world is. 1 (the default) is a single-screen
   * scene with a fixed camera. Above 1 the world extends past the viewport and
   * the camera follows the walker, which is what makes layer parallax mean
   * anything — with a fixed camera, scroll factors have nothing to act on.
   *
   * Normalised x stays 0..1 across the WORLD, not the screen.
   */
  worldWidthFactor: z.number().min(1).max(4).default(1),

  nature: NatureConfigSchema.optional(),
});
export type PaintedSceneAssetSpec = z.infer<typeof PaintedSceneAssetSpecSchema>;

/** Validate a raw JSON object against the asset spec schema; throws on mismatch. */
export function validatePaintedSceneAssetSpec(raw: unknown): PaintedSceneAssetSpec {
  return PaintedSceneAssetSpecSchema.parse(raw);
}

/**
 * Returns true when the spec uses N-bracket depth layering (any layer has
 * depthBracket or walkerRelation, or the spec defines walkerDepthBracket).
 */
export function isDepthBracketSpec(spec: PaintedSceneAssetSpec): boolean {
  if (spec.walkerDepthBracket !== undefined) return true;
  return spec.layers.some(l => l.depthBracket !== undefined || l.walkerRelation !== undefined);
}

/**
 * Resolve a layer's effective depth bracket. Falls back to walkerRelation
 * mapping or 0 when neither is specified.
 */
export function resolveLayerBracket(layer: SceneLayer, maxBracket: number): number {
  if (layer.depthBracket !== undefined) return layer.depthBracket;
  if (layer.walkerRelation === 'in-front') return maxBracket;
  if (layer.walkerRelation === 'behind') return 0;
  return 0;
}

/**
 * Compute the max depth bracket across all layers in the spec.
 */
export function computeMaxBracket(spec: PaintedSceneAssetSpec): number {
  let max = spec.walkerDepthBracket ?? 0;
  for (const layer of spec.layers) {
    if (layer.depthBracket !== undefined && layer.depthBracket > max) {
      max = layer.depthBracket;
    }
  }
  return max;
}

/**
 * Map a normalised Y (or future z-value from T6) to a depth bracket using
 * ascending thresholds. With N thresholds → N+1 brackets.
 */
export function yToDepthBracket(ny: number, thresholds: readonly number[]): number {
  for (let i = 0; i < thresholds.length; i++) {
    if (ny < thresholds[i]) return i;
  }
  return thresholds.length;
}

/**
 * Linear-interpolate a perspective scale from sparse samples. Samples are
 * sorted ascending by y; values outside the range clamp to the nearest
 * endpoint. Used by `PaintedScene` to size the walker per scene without the
 * shared `PerspectiveProfile` dance.
 *
 * When `nz` is provided (0=foreground, 1=background), it further scales the
 * result: Z=0 keeps the Y-based scale, Z=1 drives toward the smallest
 * (background) sample. This gives depth-into-scene foreshortening.
 */
export function samplePerspectiveScale(
  ny: number,
  samples: ReadonlyArray<PerspectiveSample>,
  nz?: number,
): number {
  if (samples.length === 0) return 1;
  const sorted = samples.slice().sort((a, b) => a.y - b.y);

  let yScale: number;
  if (ny <= sorted[0].y) {
    yScale = sorted[0].scale;
  } else if (ny >= sorted[sorted.length - 1].y) {
    yScale = sorted[sorted.length - 1].scale;
  } else {
    yScale = sorted[sorted.length - 1].scale;
    for (let i = 0; i < sorted.length - 1; i++) {
      const a = sorted[i];
      const b = sorted[i + 1];
      if (ny >= a.y && ny <= b.y) {
        const span = b.y - a.y;
        if (span < 1e-9) { yScale = a.scale; break; }
        const t = (ny - a.y) / span;
        yScale = a.scale + (b.scale - a.scale) * t;
        break;
      }
    }
  }

  if (nz === undefined || nz <= 0) return yScale;

  const bgScale = sorted[0].scale;
  const fgScale = sorted[sorted.length - 1].scale;
  const zScale = fgScale + (bgScale - fgScale) * nz;
  return yScale * (zScale / fgScale);
}
